import { ArtifactCategoryBadge, ArtifactTypeBadge } from '@/components/artifacts';
import type { Artifact } from '@/config/objects/artifacts';
import {
  Button,
  ConfirmDialog,
  EntityDetailShell,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Gem, Lightbulb, Pencil, Play, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Lightbulb },
  { label: 'Demo', path: 'demo', icon: Play },
] as const;

export function ArtifactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Artifact | null>(null);
  const basePath = `/artifacts/${id}`;

  const deleteMutation = useMutation({
    mutationFn: async (
      artifactId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/artifacts/${artifactId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Artifact'] });
        navigate('/artifacts');
      } else {
        throw new Error(response.error || 'Failed to delete artifact');
      }
    },
  });

  const handleDelete = () => {
    if (!pendingDelete) return;
    toast.promise(deleteMutation.mutateAsync(pendingDelete.id), {
      loading: 'Deleting artifact...',
      success: 'Artifact deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete artifact',
    });
  };

  return (
    <>
      <EntityDetailShell<Artifact>
        resource="artifacts"
        queryKey="Artifact"
        id={id}
        basePath={basePath}
        entityLabel="Artifact"
        tabs={tabs}
        contextKey="artifact"
        hero={{
          category: { label: 'Artifact', icon: Gem },
          title: (a) => a?.name || 'Artifact',
          imageUrl: (a) => a?.imageUrl ?? null,
          imageAlt: (a) => a?.name || 'Artifact image',
          fallbackIcon: Gem,
          badges: (a) => (
            <>
              {a.artifactType && (
                <ArtifactTypeBadge type={a.artifactType} variant="solid" />
              )}
              {a.artifactCategory && (
                <ArtifactCategoryBadge category={a.artifactCategory} variant="solid" />
              )}
              {a.claimMode && (
                <span className="text-sm text-white/70">
                  {a.claimMode === 'first_claim' ? 'First Claim' : 'All Teams'}
                </span>
              )}
            </>
          ),
        }}
        breadcrumbs={(a) => [
          { label: 'Home', href: '/' },
          { label: 'Artifact Library', href: '/artifacts' },
          { label: a?.name || 'Artifact', current: true },
        ]}
        headerActions={(a) =>
          a ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setPendingDelete(a);
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Artifact"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
